import { makeStyles, shorthands, tokens } from "@fluentui/react-components";

export const orderCardStyles = makeStyles({
  container: {
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    width: "100vw",
    // Page shell must be able to grow past one viewport: a fixed height clamps
    // the box while content overflows, letting the shared footer overlap it.
    minHeight: "100vh",
    // Prefer the dynamic viewport unit when supported so mobile browser chrome
    // doesn't add a phantom scroll. Falls back transparently to 100vh on
    // Safari < 15.4, Chromium < 108, Firefox < 101.
    "@supports (min-height: 100dvh)": {
      minHeight: "100dvh",
    },
    alignItems: "center",
  },
  main: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (min-width: 500px)": {
      // Fill the shell rather than measuring against it: a percentage height
      // resolves to `auto` under a min-height parent, so the region would
      // collapse to its content instead of stretching.
      flexGrow: 1,
      backgroundColor: tokens.colorNeutralBackground2,
      ...shorthands.padding("24px", "12px", "24px", "12px"),
    },
  },
  subMainContainer: {
    display: "flex",
    flexDirection: "column",
    "@media (min-width: 500px)": {
      flexDirection: "row",
      marginTop: "45px",
      // Absorb the leftover space instead of pinning to the viewport. The card
      // scrolls internally, so it must be free to shrink below its content
      // height -- hence the explicit minHeight reset for the flex item.
      flexGrow: 1,
      minHeight: 0,
      ...shorthands.borderRadius("8px"),
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow2,
      ...shorthands.overflow("hidden"),
      "::-webkit-scrollbar": {
        width: "4px",
        height: "4px",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-track": {
        backgroundColor: "#ccc",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#888",
      },
      "&:hover": {
        overflowY: "auto",
      },
    },

    "@media (min-width: 500px) and (max-width: 899px)": {
      marginLeft: "40px",
      marginRight: "40px",
    },
    "@media (min-width: 900px) and (max-width: 1239px)": {
      marginLeft: "auto",
      marginRight: "auto",
      width: "840px",
    },
    "@media (min-width: 1240px) and (max-width: 1399px)": {
      marginLeft: "200px",
      marginRight: "200px",
    },
    "@media (min-width: 1400px)": {
      marginLeft: "auto",
      marginRight: "auto",
      width: "1040px",
    },
  },
  orderBody: {
    height: "100%",
    flexDirection: "column",
    width: "100vw",
    "@media (min-width: 500px)": {
      height: "inherit",
      width: "40%",
      backgroundColor: tokens.colorNeutralBackground2,
      ...shorthands.padding("28px", "28px", "12px", "12px"),
      ...shorthands.overflow("auto"),
    },
  },
  orderSummary: {
    display: "initial",
    "@media (min-width: 500px)": {
      marginTop: "-13px",
    },
  },
  divider: {
    minHeight: "4px",
  },
  totalAmount: {
    fontWeight: "bold",
    textAlign: "right",
    "@media (max-width: 499px)": {
      ...shorthands.padding("9px", "30px", "9px", "0px"),
    },
    "@media (min-width: 500px)": {
      textAlign: "right",
      ...shorthands.padding("9px", "18px", "9px", "0px"),
    },
  },
  reviewLine: {
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    paddingLeft: "16px",
    paddingTop: "10px",
    paddingBottom: "10px",
    height: "35px",
    backgroundColor: tokens.colorNeutralBackground3,
    "@media (min-width: 500px)": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  itemList: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("1px"),
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (min-width: 500px)": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },

  amountBody: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
    "@media (min-width: 500px)": {
      flexDirection: "row",
      marginBottom: "8px",
    },
  },
  subTitle: {
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    paddingLeft: "16px",
    paddingTop: "10px",
    paddingBottom: "10px",
    height: "35px",
    display: "none",
    "@media (min-width: 500px)": {
      display: "block",
      fontSize: "20px",
    },
  },
  title: {
    display: "block",
    "@media (min-width: 500px)": {
      display: "none",
    },
  },
  itemAmount: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    alignSelf: "flex-start",
    "@media (max-width: 500px)": {
      ...shorthands.padding("0px", "10px", "0px", "0px"),
    },
  },
  taxAmount: {
    fontWeight: tokens.fontWeightRegular,
    textAlign: "right",
    "@media (max-width: 499px)": {
      ...shorthands.padding("9px", "30px", "9px", "0px"),
    },
    "@media (min-width: 500px)": {
      color: tokens.colorNeutralForeground3,
      ...shorthands.padding("9px", "18px", "9px", "0px"),
    },
  },
});
